import { AddNotificationModal } from '@/components/common/AddNotificationModal';
import { DataTable } from '@/components/common/DataTable';
import { useHeader } from '@/components/context/HeaderContext';
import { DateRange } from '@/types';
import { truncate } from '@/utils/truncate';
import { ColumnDef } from '@tanstack/react-table';
import { ExternalLink } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetNotificationQuery } from '@/store/api/notificationApi';

type NotificationTableRow = {
  id: string;
  title: string;
  message: string;
  link: string;
  createdAt: string;
  totalRecipients: number;
};

type NotificationItem = {
  id?: number | string;
  notificationCode?: string | null;
  title?: string | null;
  message?: string | null;
  link?: string | null;
  createdAt?: string | null;
  totalRecipients?: number | null;
  data?: {
    title?: string | null;
    message?: string | null;
    link?: string | null;
    totalRecipients?: number | null;
  } | null;
};

type NotificationResponseShape = {
  data?: NotificationItem[];
  notifications?: NotificationItem[];
  count?: number;
  totalCount?: number;
};

const formatDate = (date?: Date) => {
  if (!date) return undefined;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (value: string) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getColumns = (): ColumnDef<NotificationTableRow>[] => [
  {
    accessorKey: 'title',
    header: 'Title',
    cell: ({ row }) => (
      <div className="flex flex-col gap-1 max-w-[300px]">
        <div className="font-semibold text-sm text-[#1E0036] break-words min-w-[150px]">
          {row.original.title}
        </div>
        <div className="text-[12px] text-[#8E87A4] ">{row.original.message}</div>
      </div>
    ),
  },
  {
    accessorKey: 'createdAt',
    header: 'Created Date',
    cell: ({ row }) => <span className="text-[#1E0036]">{row.original.createdAt}</span>,
  },
  {
    accessorKey: 'totalRecipients',
    header: 'Recipients',
    cell: ({ row }) => (
      <span className="text-[#1E0036] font-medium">{row.original.totalRecipients}</span>
    ),
  },
  {
    accessorKey: 'link',
    header: 'Links',
    cell: ({ row }) =>
      row.original.link ? (
        <a
          href={row.original.link}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-[#3C82F6] hover:opacity-70 transition-opacity"
        >
          <ExternalLink className="w-5 h-5" />
        </a>
      ) : (
        <span className="text-[#8E87A4]">—</span>
      ),
  },
];

export default function AddNotification() {
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const fromDate = formatDate(dateRange.from);
  const toDate = formatDate(dateRange.to);

  const queryParams = useMemo(
    () => ({
      skip: (page - 1) * take,
      take,
      search: search.trim() || undefined,
      fromDate,
      toDate,
      notificationCode: 'SYSTEM_CUSTOM',
    }),
    [page, take, search, fromDate, toDate]
  );

  const { data, refetch } = useGetNotificationQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  const responseData = data as NotificationResponseShape | undefined;
  const apiNotifications = responseData?.data ?? responseData?.notifications ?? [];

  const notifications = useMemo<NotificationTableRow[]>(
    () =>
      (apiNotifications as NotificationItem[]).map((item) => ({
        id: String(item.id ?? ''),
        title: item.title ?? item.data?.title ?? '—',
        message: item.message ?? item.data?.message ?? '—',
        link: item.link ?? item.data?.link ?? '',
        createdAt: formatDisplayDate(item.createdAt ?? ''),
        totalRecipients: item.totalRecipients ?? item.data?.totalRecipients ?? 0,
      })),
    [apiNotifications]
  );

  const totalCount =
    responseData?.count ?? responseData?.totalCount ?? apiNotifications.length ?? 0;
  const columns = useMemo(() => getColumns(), []);

  const handleNotificationCreated = useCallback(() => {
    if (page !== 1) {
      setPage(1);
      return;
    }

    refetch();
  }, [page, refetch]);

  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateRange(range);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleTakeChange = useCallback((value: number) => {
    setTake(value);
    setPage(1);
  }, []);

  const { setHeader } = useHeader();
  useEffect(() => {
    setHeader({
      type: 'user',
      props: {
        title: 'Add Notification',
        showSort: true,
        showFilter: true,
        showMore: true,
      },
    });
    return () => setHeader(null);
  }, [setHeader]);
  return (
    <div className="p-3 sm:p-6">
      <DataTable
        data={notifications}
        columns={columns}
        title="Notifications"
        addButtonText="Add New Notification"
        onAddClick={openModal}
        totalCount={totalCount}
        isServerPaginated
        page={page}
        take={take}
        search={search}
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        onPageChange={setPage}
        onTakeChange={handleTakeChange}
        enableAddNewBtn={true}
        enableSearch
        searchPlaceholder="Search by title"
        onSearchChange={handleSearchChange}
      />
      {isModalOpen && (
        <AddNotificationModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSuccess={handleNotificationCreated}
          notificationCode="SYSTEM_CUSTOM"
        />
      )}
    </div>
  );
}
